/* eslint-disable no-underscore-dangle */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, Form, Button, Table, Spinner, Card,
} from 'react-bootstrap';
import Error from '../../../Components/Error';
import PaymentsService from '../../../Services/PaymentsService';
import { useAccountsList } from '../../../Hooks/useAccountsCache';
import { formatDateTime } from '../../../Utils/timezone';
import occurredAtIso from '../../../Utils/occurredAt';

const RECONCILED_STATUSES = ['completed', 'reconciled'];

function formatMoney(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const n = Number(amount);
  if (currency === 'HUF') return `${Math.round(n).toLocaleString('en-US')} Ft`;
  return `€${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function todayDateString() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const defaultForm = () => ({
  amount: '',
  currency: 'EUR',
  occurredAt: todayDateString(),
  bankReference: '',
  notes: '',
  accountId: '',
});

function PartnerPaymentsModal({
  show = false, onHide, partner = { id: '', name: '' },
}) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showRecordForm, setShowRecordForm] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Accounts (active only, used for the inline picker) + a byId map for
  // looking up account names in the rendered history table.
  const { accounts: allAccounts, byId: accountsById } = useAccountsList();
  const activeAccounts = useMemo(
    () => allAccounts.filter((a) => a.is_active),
    [allAccounts],
  );
  const defaultAccountId = useMemo(() => {
    const bank = activeAccounts.find((a) => a.kind === 'bank');
    if (bank) return bank._id;
    return activeAccounts[0]?._id || '';
  }, [activeAccounts]);

  const fetchPayments = async () => {
    if (!partner?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await PaymentsService.listForCounterparty('partner', partner.id);
      setPayments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && partner?.id) {
      fetchPayments();
    } else if (!show) {
      // Reset transient UI state when the modal closes so re-opens are clean.
      setShowRecordForm(false);
      setForm(defaultForm());
      setSubmitError(null);
      setSuccessMessage('');
    }
  }, [show, partner?.id]);

  // Default-pick the first active bank account once the cache resolves,
  // but only if the admin hasn't already chosen one.
  useEffect(() => {
    if (!show) return;
    if (form.accountId) return;
    if (defaultAccountId) {
      setForm((prev) => ({ ...prev, accountId: defaultAccountId }));
    }
  }, [show, defaultAccountId, form.accountId]);

  // Auto-dismiss the inline success message after 3s.
  useEffect(() => {
    if (!successMessage) return undefined;
    const t = setTimeout(() => setSuccessMessage(''), 3000);
    return () => clearTimeout(t);
  }, [successMessage]);

  // Sum of incoming + reconciled/completed payments in the current
  // calendar year. Group by currency since EUR and HUF can't be added.
  const totalsThisYearByCurrency = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return payments.reduce((acc, p) => {
      if (!p) return acc;
      if (p.direction !== 'in') return acc;
      if (!RECONCILED_STATUSES.includes(p.status)) return acc;
      if (!p.occurred_at) return acc;
      const y = new Date(p.occurred_at).getFullYear();
      if (y !== currentYear) return acc;
      const cur = p.currency || 'EUR';
      const gross = Number(p.gross_amount) || 0;
      acc[cur] = (acc[cur] || 0) + gross;
      return acc;
    }, {});
  }, [payments]);

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const resetForm = () => {
    setForm(defaultForm());
    setSubmitError(null);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        direction: 'in',
        account_id: form.accountId,
        gross_amount: Number(form.amount),
        fee: 0,
        currency: form.currency,
        occurred_at: occurredAtIso(form.occurredAt),
        counterparty: { kind: 'partner', id: partner.id },
        external_refs: form.bankReference
          ? { bank_reference: form.bankReference }
          : undefined,
        notes: form.notes || undefined,
        lines: [],
      };
      const created = await PaymentsService.recordPayment(payload);
      if (created) {
        setPayments((prev) => [created, ...prev]);
      } else {
        // If the API doesn't echo a row, re-fetch to stay consistent.
        fetchPayments();
      }
      resetForm();
      setShowRecordForm(false);
      setSuccessMessage('✓ Recorded');
    } catch (err) {
      setSubmitError(err.response?.data?.message || err.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const totalsEntries = Object.entries(totalsThisYearByCurrency);

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Payments
          {partner?.name ? ` — ${partner.name}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap">
          <div>
            <div className="text-muted small">Total received this year</div>
            {totalsEntries.length === 0 ? (
              <div className="fw-bold">{formatMoney(0, 'EUR')}</div>
            ) : (
              totalsEntries.map(([cur, sum]) => (
                <div key={cur} className="fw-bold">
                  {formatMoney(sum, cur)}
                </div>
              ))
            )}
          </div>
          <div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setShowRecordForm((prev) => !prev);
                setSubmitError(null);
              }}
            >
              <i className="fa fa-receipt me-2" />
              {showRecordForm ? 'Hide form' : 'Record Wire Received'}
            </Button>
          </div>
        </div>

        {successMessage && (
          <div className="alert alert-success py-2 px-3 mb-3" role="status">
            {successMessage}
          </div>
        )}

        {showRecordForm && (
          <Card className="mb-3">
            <Card.Body>
              <Card.Title as="h6" className="mb-3">Record Wire Received</Card.Title>

              {submitError && (
                <div className="alert alert-danger py-2 px-3" role="alert">
                  {submitError}
                </div>
              )}

              <Form onSubmit={handleSubmit}>
                <div className="row">
                  <Form.Group className="mb-3 col-md-4" controlId="amount">
                    <Form.Label>Amount *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={form.amount}
                      onChange={handleFieldChange('amount')}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-4" controlId="currency">
                    <Form.Label>Currency *</Form.Label>
                    <Form.Select
                      value={form.currency}
                      onChange={handleFieldChange('currency')}
                    >
                      <option value="EUR">EUR</option>
                      <option value="HUF">HUF</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-4" controlId="occurredAt">
                    <Form.Label>Date received *</Form.Label>
                    <Form.Control
                      type="date"
                      value={form.occurredAt}
                      onChange={handleFieldChange('occurredAt')}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6" controlId="accountId">
                    <Form.Label>Account *</Form.Label>
                    <Form.Select
                      value={form.accountId}
                      onChange={handleFieldChange('accountId')}
                    >
                      <option value="">Select account…</option>
                      {activeAccounts.map((a) => (
                        <option key={a._id} value={a._id}>
                          {a.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6" controlId="bankReference">
                    <Form.Label>Bank reference</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.bankReference}
                      onChange={handleFieldChange('bankReference')}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3 col-md-6" controlId="notes">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      value={form.notes}
                      onChange={handleFieldChange('notes')}
                    />
                  </Form.Group>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowRecordForm(false);
                      resetForm();
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={submitting || !form.amount || !form.accountId}
                  >
                    {submitting ? 'Submitting…' : 'Record payment'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" size="sm" />
            <div className="text-muted small mt-2">Loading payments…</div>
          </div>
        ) : (
          <Table size="sm" hover responsive>
            <thead>
              <tr>
                <th>Date</th>
                <th>Direction</th>
                <th>Account</th>
                <th>Gross</th>
                <th>Currency</th>
                <th>Bank Ref</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-3">
                    No payments recorded for this partner.
                  </td>
                </tr>
              ) : (
                payments.map((p) => {
                  const acc = p.account_id ? accountsById.get(p.account_id) : null;
                  return (
                    <tr key={p.id || p._id || `${p.occurred_at}-${p.gross_amount}-${p.bank_reference || ''}`}>
                      <td>{p.occurred_at ? formatDateTime(p.occurred_at) : '—'}</td>
                      <td>{p.direction || '—'}</td>
                      <td>{acc ? acc.name : '—'}</td>
                      <td>{formatMoney(p.gross_amount, p.currency)}</td>
                      <td>{p.currency || '—'}</td>
                      <td>{p.external_refs?.bank_reference || '—'}</td>
                      <td>{p.notes || '—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default PartnerPaymentsModal;
